import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/marketing/components/ui/button';
import { Input } from '@/marketing/components/ui/input';
import { campaignInfluencerTagAPIClient } from '@/marketing/infrastructure/influencer/campaign/tag/campaignInfluencerTagAPIClient';
import { CAMPAIGN_INFLUENCER_TAG_KEYS } from '@/marketing/infrastructure/influencer/campaign/tag/utils/keys';
import { CAMPAIGN_INFLUENCER_TAG_QUERIES } from '@/marketing/infrastructure/influencer/campaign/tag/utils/queries';
import { extractApiErrorMessage } from '@/marketing/lib/extractApiErrorMessage';
import {
  isDefaultCampaignInfluencerTagName,
  MAX_CAMPAIGN_INFLUENCER_TAG_NAME_LENGTH,
} from '@/marketing/models/campaignInfluencerTag';
import { CampaignInfluencerTagManagerRow } from './CampaignInfluencerTagManagerRow';

interface CampaignInfluencerTagManagerProps {
  campaignId: string;
  assignedTagIds: string[];
  isAssignmentPending: boolean;
  onToggleAssignment: (tagId: string) => void;
}

// Campaign tags and the shared global catalog also toggle assignment on the open influencer.
export function CampaignInfluencerTagManager({
  campaignId,
  assignedTagIds,
  isAssignmentPending,
  onToggleAssignment,
}: CampaignInfluencerTagManagerProps) {
  const queryClient = useQueryClient();
  const { data: tags = [], isLoading, isError } = useQuery(CAMPAIGN_INFLUENCER_TAG_QUERIES.list(campaignId));

  const [isAdding, setIsAdding] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagScope, setNewTagScope] = useState('campaign');
  const isDefaultTagName = isDefaultCampaignInfluencerTagName(newTagName);

  const invalidateTagCatalog = () =>
    queryClient.invalidateQueries({
      queryKey: CAMPAIGN_INFLUENCER_TAG_KEYS.all,
    });

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      campaignInfluencerTagAPIClient.createTag({
        name,
        ...(newTagScope === 'campaign' || isDefaultCampaignInfluencerTagName(name) ? { campaignId } : {}),
      }),
    onSuccess: (createdTag) => {
      setNewTagName('');
      setNewTagScope('campaign');
      setIsAdding(false);
      onToggleAssignment(createdTag.tagId);
      toast.success('Tag created');
      return invalidateTagCatalog();
    },
    onError: (error: unknown) => toast.error(extractApiErrorMessage(error, 'Failed to create tag')),
  });

  const renameMutation = useMutation({
    mutationFn: ({ tagId, name }: { tagId: string; name: string }) =>
      campaignInfluencerTagAPIClient.renameTag(tagId, { name }, campaignId),
    onSuccess: () => {
      toast.success('Tag renamed');
      return invalidateTagCatalog();
    },
    onError: (error: unknown) => toast.error(extractApiErrorMessage(error, 'Failed to rename tag')),
  });

  const deleteMutation = useMutation({
    mutationFn: (tagId: string) => campaignInfluencerTagAPIClient.deleteTag(tagId, campaignId),
    onSuccess: () => {
      toast.success('Tag deleted');
      return invalidateTagCatalog();
    },
    onError: (error: unknown) => toast.error(extractApiErrorMessage(error, 'Failed to delete tag')),
  });

  const isCatalogMutating =
    isAssignmentPending || createMutation.isPending || renameMutation.isPending || deleteMutation.isPending;

  const handleCreate = () => {
    const trimmedName = newTagName.trim();
    if (trimmedName.length === 0 || isCatalogMutating) return;

    createMutation.mutate(trimmedName);
  };

  const handleCancelAdd = () => {
    setNewTagName('');
    setNewTagScope('campaign');
    setIsAdding(false);
  };

  return (
    <div className="tw-w-80">
      <div className="tw-max-h-64 tw-overflow-auto tw-p-1">
        {isLoading ? (
          <div className="tw-px-2 tw-py-3 tw-text-center tw-text-[12px] tw-text-muted-foreground">Loading tags…</div>
        ) : isError ? (
          <div className="tw-px-2 tw-py-3 tw-text-center tw-text-[12px] tw-text-destructive">
            Couldn’t load tags. Please try again.
          </div>
        ) : tags.length === 0 ? (
          <div className="tw-px-2 tw-py-3 tw-text-center tw-text-[12px] tw-text-muted-foreground">
            No tags yet. Add one below.
          </div>
        ) : (
          tags.map((tag) => (
            <CampaignInfluencerTagManagerRow
              key={tag.tagId}
              tag={tag}
              isAssigned={assignedTagIds.includes(tag.tagId)}
              isMutating={isCatalogMutating}
              onToggleAssignment={onToggleAssignment}
              onRename={(tagId, name) => renameMutation.mutate({ tagId, name })}
              onDelete={(tagId) => deleteMutation.mutate(tagId)}
            />
          ))
        )}
      </div>

      <div className="tw-border-t tw-p-1.5">
        {isAdding ? (
          <div className="tw-space-y-1.5">
            <label className="tw-flex tw-items-center tw-gap-1.5 tw-px-1 tw-text-[12px] tw-text-muted-foreground">
              <input
                type="checkbox"
                checked={!isDefaultTagName && newTagScope === 'global'}
                onChange={(event) => setNewTagScope(event.target.checked ? 'global' : 'campaign')}
                disabled={isCatalogMutating || isDefaultTagName}
              />
              Global
            </label>
            {isDefaultTagName && (
              <p className="tw-m-0 tw-text-[11px] tw-text-muted-foreground">Default tags cannot be global.</p>
            )}
            <div className="tw-flex tw-items-center tw-gap-1">
              <Input
                autoFocus
                value={newTagName}
                onChange={(event) => {
                  const name = event.target.value;
                  setNewTagName(name);
                  if (isDefaultCampaignInfluencerTagName(name)) setNewTagScope('campaign');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleCreate();
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    handleCancelAdd();
                  }
                }}
                maxLength={MAX_CAMPAIGN_INFLUENCER_TAG_NAME_LENGTH}
                placeholder="New tag name…"
                className="tw-h-7 tw-text-[13px]"
              />
              <Button
                variant="ghost"
                size="sm"
                className="tw-h-7 tw-w-7 tw-flex-shrink-0 tw-p-0 tw-text-emerald-600"
                disabled={newTagName.trim().length === 0 || isCatalogMutating}
                onClick={handleCreate}
                aria-label="Create tag"
              >
                <Check className="tw-h-3.5 tw-w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="tw-h-7 tw-w-7 tw-flex-shrink-0 tw-p-0 tw-text-muted-foreground"
                onClick={handleCancelAdd}
                aria-label="Cancel add tag"
              >
                <X className="tw-h-3.5 tw-w-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="tw-w-full tw-justify-start tw-gap-1.5 tw-text-[13px] tw-text-muted-foreground"
            disabled={isCatalogMutating}
            onClick={() => setIsAdding(true)}
          >
            <Plus className="tw-h-3.5 tw-w-3.5" /> Add tag
          </Button>
        )}
      </div>
    </div>
  );
}
